import { useEffect, useRef, useState } from "react";
import { Search, LogOut, KeyRound, RotateCcw, CalendarDays } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../auth/AuthProvider";
import { useBusinessDate } from "../lib/businessDate";
import { RoleChip } from "./RoleChip";

/* Single-letter avatar — first letter of the username, with a colour seeded
 * off the user id so different users look distinct without us inventing a
 * colour-mapping config. */
function avatarColors(uid: number) {
  const palette = [
    { bg: "#1d3a73", fg: "#f0e7c6" }, // ink-blue
    { bg: "#c34a3e", fg: "#fbf7ec" }, // vermilion
    { bg: "#8b6f47", fg: "#fbf7ec" }, // sepia
    { bg: "#2a251f", fg: "#f3ebd8" }, // dark ink
    { bg: "#4a4034", fg: "#f3ebd8" },
  ];
  return palette[uid % palette.length] ?? palette[0]!;
}

export function TopBar() {
  const { me, signOut } = useAuth();
  const navigate = useNavigate();
  const { date, setDate, today } = useBusinessDate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  if (!me) return null;
  const initial = (me.username || "?").charAt(0).toUpperCase();
  const color = avatarColors(me.user_id);
  const isCustomDate = !!date && date !== today;

  return (
    <header
      className="sticky top-0 z-30 border-b"
      style={{ background: "var(--bg)", borderColor: "var(--rule)" }}
    >
      <div className="px-5 md:px-8 py-1.5 flex items-center gap-3">
        {/* ── Left: date picker ──────────────────────────────────── */}
        <label
          className="flex items-center gap-1.5 px-2 py-1 border cursor-pointer relative"
          style={{
            borderColor: isCustomDate ? "var(--accent)" : "var(--rule)",
            background: "var(--bg-card)",
          }}
          title="Business date — pick any day to inspect"
        >
          <CalendarDays size={13}
            className={isCustomDate ? "text-[var(--accent)]" : "text-[var(--fg-muted)]"} />
          <input
            type="date"
            value={date ?? today}
            max={today}
            onChange={(e) => setDate(e.target.value || null)}
            className="bg-transparent border-0 outline-none font-mono text-[12px] text-[var(--fg)] cursor-pointer"
            style={{ width: "120px" }}
          />
          {isCustomDate && (
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); setDate(null); }}
              aria-label="Clear date — show latest"
              className="np-btn-ghost p-1"
              title="Clear — auto"
            >
              <RotateCcw size={11} />
            </button>
          )}
        </label>

        {/* ── Middle: search (slim, disabled placeholder for M1) ── */}
        <div className="flex-1 max-w-md">
          <div
            className="flex items-center gap-2 px-2.5 py-1 border opacity-60 cursor-not-allowed"
            style={{ borderColor: "var(--rule)" }}
            aria-label="Search (coming soon)"
          >
            <Search size={12} className="text-[var(--fg-faint)] shrink-0" />
            <input
              type="text"
              placeholder="Search publishers, editions, dates — soon"
              disabled
              className="bg-transparent w-full text-[12px] font-sans outline-none placeholder:text-[var(--fg-faint)] truncate"
            />
          </div>
        </div>

        {/* spacer for symmetry on narrow screens */}
        <div className="hidden lg:block flex-1" />

        {/* ── Right: compact avatar that expands a menu ───────────── */}
        <div className="relative" ref={ref}>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-haspopup="menu"
            aria-expanded={open}
            aria-label={`Account menu — ${me.username}`}
            className="w-8 h-8 inline-flex items-center justify-center font-display font-semibold text-[15px] transition-shadow hover:shadow-plate"
            style={{
              background: color.bg,
              color: color.fg,
              borderRadius: 0,
            }}
            title={`${me.username} · ${me.group_name}`}
          >
            {initial}
          </button>

          {open && (
            <div
              role="menu"
              className="absolute right-0 mt-1.5 w-72 np-card shadow-plate animate-fade-up"
            >
              <div className="px-4 py-3 border-b flex items-center gap-3"
                   style={{ borderColor: "var(--rule)" }}>
                <div
                  className="w-10 h-10 inline-flex items-center justify-center font-display font-semibold text-[18px] shrink-0"
                  style={{ background: color.bg, color: color.fg }}
                >
                  {initial}
                </div>
                <div className="min-w-0">
                  <div className="font-display text-[16px] font-medium leading-tight truncate">
                    {me.username}
                  </div>
                  {me.email && (
                    <div className="font-mono text-[11px] text-[var(--fg-muted)] truncate">
                      {me.email}
                    </div>
                  )}
                  <div className="mt-1">
                    <RoleChip group={me.group_name} />
                  </div>
                </div>
              </div>

              <button
                type="button"
                role="menuitem"
                onClick={() => { setOpen(false); navigate("/change-password"); }}
                className="w-full px-4 py-2.5 text-left text-[13px] font-sans flex items-center gap-2 hover:bg-[var(--bg-inset)]"
              >
                <KeyRound size={14} />
                Change password
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => { setOpen(false); void signOut(); }}
                className="w-full px-4 py-2.5 text-left text-[13px] font-sans flex items-center gap-2 text-[var(--accent)] hover:bg-[var(--accent-soft)] border-t"
                style={{ borderColor: "var(--rule)" }}
              >
                <LogOut size={14} />
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
